import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { ArrowSquareOut, DotsThreeVertical } from "@phosphor-icons/react"
import { t } from "i18next"
import {
  Card,
  CardBody,
  CardBodyContent,
  CardBodyHeader,
  CardBodyHeaderTitle,
  CardHeader,
  CardHeaderTitle,
} from "@/components/ui/card.tsx"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { List, ListItem, ListText } from "@/components/ui/list"
import { ageFormat } from "@/lib/age-format"
import { formatDate } from "@/lib/format-date"
import { GenderIcon } from "@/modules/patients/components/gender-icon"
import { PatientData } from "@/modules/patients/detail/store/usePatientStore"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { PatientFragment } from "@/modules/patients/services/PatientFragment"

type Profile = PatientFragment$key
type AppProps = {
  appAccount: NonNullable<PatientData>["account"]
}

const AppProfile = ({ profileRef }: { profileRef: Profile }) => {
  const currentPatientId = usePatientParam()
  const profile = useFragment<PatientFragment$key>(PatientFragment, profileRef)
  const { firstName, lastName, gender, birthDate, age, id, country } = profile
  const isNotCurrentPatientId = currentPatientId !== id

  const handleOpenMedicalRecord = () => window.open(`/patients/${country}/${id}`, id)

  return (
    <ListItem
      data-testid="app-profile-item"
      className={`group relative min-h-8 items-center gap-8 ${isNotCurrentPatientId ? "hover:bg-surface-hovered hover:cursor-pointer" : ""}`}>
      <div className="flex gap-2">
        <GenderIcon gender={gender} className="h-10 w-10 shrink-0" />
        <div className="min-w-0">
          <ListText>
            {lastName}, {firstName}
          </ListText>
          <ListText variant="secondary">
            {formatDate(new Date(birthDate))} {age && `(${ageFormat({ age })})`}
          </ListText>
        </div>
      </div>
      {isNotCurrentPatientId && <AppProfileMenu onOpenMedicalRecord={handleOpenMedicalRecord} />}
    </ListItem>
  )
}

const AppProfileMenu = ({ onOpenMedicalRecord }: { onOpenMedicalRecord: () => void }) => {
  const { t } = useTranslation()

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <div className="hover:bg-surface-hovered ml-auto p-2 hover:rounded-full hover:p-2">
          <DotsThreeVertical className="text-on-surface-subdued invisible group-hover:visible" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        data-testid="app-profile-options-content"
        className="min-w-75 rounded-lg p-2"
        onCloseAutoFocus={(e) => e.preventDefault()}>
        <DropdownMenuItem
          data-testid="open-medical-record-menu-item"
          className="text-on-surface focus:bg-surface-hovered gap-2 rounded-md"
          onClick={onOpenMedicalRecord}>
          <ArrowSquareOut className="text-on-surface-subdued" data-testid="open-medical-record-icon" />
          {t("patient.ibc.openMedicalRecord")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const AppCardSection = ({ sectionName, children }: { sectionName: string; children: React.ReactNode }) => (
  <CardBody>
    <CardBodyHeader>
      <CardBodyHeaderTitle>{sectionName}</CardBodyHeaderTitle>
    </CardBodyHeader>
    <CardBodyContent>{children}</CardBodyContent>
  </CardBody>
)

const App = ({ appAccount }: AppProps) => {
  const { mainProfile, childProfiles, accountVerification } = appAccount ?? {}

  if (!appAccount || accountVerification?.status !== "VERIFIED") return null

  return (
    <Card className="animate-fade-in-top w-full" data-testid={"patient-information-app"}>
      <CardHeader>
        <div className="flex items-center gap-4">
          <CardHeaderTitle data-testid="card-header-title-content">{t("patient.information.app.app")}</CardHeaderTitle>
        </div>
      </CardHeader>
      {mainProfile && (
        <AppCardSection sectionName={t("patient.information.app.mainPatient")}>
          <AppProfile profileRef={mainProfile.patient} />
        </AppCardSection>
      )}
      {!!childProfiles?.length && (
        <CardBody>
          <CardBodyHeader>
            <CardBodyHeaderTitle>{t("patient.information.app.children")}</CardBodyHeaderTitle>
          </CardBodyHeader>
          <List className="px-4 pb-4">
            {childProfiles.map(({ patient }) => (
              <AppProfile profileRef={patient} key={patient.id} />
            ))}
          </List>
        </CardBody>
      )}
    </Card>
  )
}
export { App }
